import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Abas, PainelAba } from '../../componentes/abas';
import { Alternador } from '../../componentes/formulario';
import { rotuloModulo } from '../../nucleo/rotulos';
import { descricaoDaTool } from '../../nucleo/descricao-tool';
import { ShieldOff } from 'lucide-react';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { Confirmar, Modal } from '../../componentes/sobreposicao';
import { TextoCortado } from '../../componentes/texto-cortado';
import { useToast } from '../../componentes/toast';
import { get, mensagemDeErro, api } from '../../nucleo/api';
import { podeNoWorkspace, useSessao, veTodasAsEmpresas } from '../../nucleo/sessao';
import { nomePessoa } from '../../nucleo/formato';
import { escopoLegivel } from '../../paginas/conta-assistentes';
import { useChaveRecente } from './chave-recente';
import { BlocoCodigo, Chips, Json, LinhaCopiavel, ListaPaginada, Quando } from './comum';
import type { ChamadaMcp, ConexaoOAuth, InfoMcp, OAuthMcp, ToolMcp } from './tipos';

const PLACEHOLDER = 'erp_live_...';

// Lote 19/10 (item 14): o MCP do MalgoOS é o padrão (Streamable HTTP com a chave no Authorization), então serve a qualquer
// cliente MCP; aqui vão os trechos das IAs mais usadas. O app do ChatGPT e o Claude.ai ligam servidores remotos por OAuth:
// para eles basta a URL (o cliente descobre o resto pelo 401 do /mcp), com o OAuth permitido no workspace.
const AGENTES = ['claude', 'claudeai', 'chatgpt', 'codex', 'gemini', 'openai', 'grok', 'openclaw', 'hermes', 'outros'] as const;
type Agente = (typeof AGENTES)[number];
/** Clientes que conectam por OAuth: a aba mostra só a URL. */
export const AGENTES_OAUTH: readonly Agente[] = ['claudeai', 'chatgpt'];

/** Snippets no formato de specs/INTEGRACOES-API-MCP.md, já com a URL do tenant e a chave. */
export function montarSnippets(url: string, chave: string) {
  return {
    claudeComando: `claude mcp add --transport http erp ${url} \\\n  --header "Authorization: Bearer ${chave}"`,
    claudeJson: `{ "mcpServers": { "erp": { "type": "http", "url": "${url}",\n  "headers": { "Authorization": "Bearer ${chave}" } } } }`,
    openclaw: `{ "mcpServers": { "erp": { "transport": "streamable-http", "url": "${url}",\n  "headers": { "Authorization": "Bearer ${chave}" } } } }`,
    hermes: `mcp_servers:\n  erp:\n    url: ${url}\n    headers:\n      Authorization: "Bearer ${chave}"`,
    codex: `[mcp_servers.erp]\nurl = "${url}"\nhttp_headers = { "Authorization" = "Bearer ${chave}" }`,
    gemini: `{ "mcpServers": { "erp": { "httpUrl": "${url}",\n  "headers": { "Authorization": "Bearer ${chave}" } } } }`,
    openai: `{ "type": "mcp", "server_label": "erp", "server_url": "${url}",\n  "headers": { "Authorization": "Bearer ${chave}" }, "require_approval": "always" }`,
    grok: `{ "type": "mcp", "server_label": "erp", "server_url": "${url}",\n  "headers": { "Authorization": "Bearer ${chave}" } }`,
    outros: `url: ${url}\ntransport: streamable-http\nheaders:\n  Authorization: Bearer ${chave}`,
  };
}

function Snippets({ url, oauthLigado }: { url: string; oauthLigado: boolean | null }) {
  const { t } = useTranslation('integracoes');
  const recente = useChaveRecente();
  const [agente, setAgente] = useState<Agente>('claude');
  const sn = useMemo(() => montarSnippets(url, recente ?? PLACEHOLDER), [url, recente]);
  return (
    <Cartao titulo={t('mcp.snippets.titulo')}>
      <div className="flex min-w-0 flex-col gap-3">
        {!AGENTES_OAUTH.includes(agente) && <p className="text-sm text-tinta-3">{recente ? t('mcp.snippets.comChave') : t('mcp.snippets.semChave')}</p>}
        {!recente && !AGENTES_OAUTH.includes(agente) && (
          <Link to="/integracoes/integracoes/api" className="w-fit text-sm font-medium text-acento-forte hover:underline">
            {t('mcp.snippets.criarChave')}
          </Link>
        )}
        <Abas idBase="mcp-agentes" fundo="bg-sup-2" rotulo={t('mcp.snippets.titulo')} ativa={agente} aoMudar={setAgente} itens={AGENTES.map((a) => ({ chave: a, rotulo: t(`mcp.snippets.agentes.${a}`) }))} />
        <PainelAba idBase="mcp-agentes" chave={agente} className="flex min-w-0 flex-col gap-3">
        {AGENTES_OAUTH.map(
          (a) =>
            agente === a && (
              <div key={a} className="flex min-w-0 flex-col gap-3" data-agente-oauth={a}>
                <p className="text-sm text-tinta-3">{t(`mcp.snippets.notas.${a}`)}</p>
                {oauthLigado === false && (
                  <p role="status" className="rounded-lg border border-laranja px-3 py-2 text-sm text-laranja">
                    {t('mcp.oauth.desligadoAviso')}
                  </p>
                )}
                <span className="kicker">{t(`mcp.snippets.onde.${a}`)}</span>
                <LinhaCopiavel texto={url} />
              </div>
            ),
        )}
        {agente === 'claude' && (
          <>
            <span className="kicker">{t('mcp.snippets.comando')}</span>
            <BlocoCodigo codigo={sn.claudeComando} rotulo={t('mcp.snippets.comando')} />
            <span className="kicker">{t('mcp.snippets.arquivo', { arquivo: '.mcp.json' })}</span>
            <BlocoCodigo codigo={sn.claudeJson} rotulo={t('mcp.snippets.arquivo', { arquivo: '.mcp.json' })} />
          </>
        )}
        {agente === 'openclaw' && (
          <>
            <span className="kicker">{t('mcp.snippets.arquivoOpenclaw')}</span>
            <BlocoCodigo codigo={sn.openclaw} rotulo={t('mcp.snippets.arquivoOpenclaw')} />
          </>
        )}
        {(['codex', 'gemini', 'openai', 'grok', 'outros'] as const).map(
          (a) =>
            agente === a && (
              <div key={a} className="flex min-w-0 flex-col gap-3">
                <p className="text-sm text-tinta-3">{t(`mcp.snippets.notas.${a}`)}</p>
                <span className="kicker">{t(`mcp.snippets.onde.${a}`)}</span>
                <BlocoCodigo codigo={sn[a]} rotulo={t(`mcp.snippets.onde.${a}`)} />
              </div>
            ),
        )}
        {agente === 'hermes' && (
          <>
            <span className="kicker">{t('mcp.snippets.arquivo', { arquivo: '~/.hermes/config.yaml' })}</span>
            <BlocoCodigo codigo={sn.hermes} rotulo={t('mcp.snippets.arquivo', { arquivo: '~/.hermes/config.yaml' })} />
          </>
        )}
        </PainelAba>
      </div>
    </Cartao>
  );
}

function LinhaTool({ tool }: { tool: ToolMcp }) {
  const { t } = useTranslation('integracoes');
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const alternar = useMutation({
    mutationFn: (ligada: boolean) => api('PUT', `/integracoes/mcp/tools/${encodeURIComponent(tool.nome)}`, { ligada }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integracoes', 'mcp'] }),
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <li className="flex min-w-0 items-center gap-3 py-2.5">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <code className={`font-mono text-sm ${tool.ligada ? 'text-tinta-1' : 'text-tinta-3 line-through'}`}>{tool.nome}</code>
          <Selo cor={tool.tipo === 'leitura' ? 'azul' : 'laranja'}>{t(`mcp.tools.${tool.tipo}`)}</Selo>
        </div>
        <p className="mt-0.5 text-xs text-tinta-3">{descricaoDaTool(tool.nome, tool.descricao)}</p>
      </div>
      <Alternador
        rotulo={`${tool.ligada ? t('mcp.tools.desligar') : t('mcp.tools.ligar')} ${tool.nome}`}
        marcado={tool.ligada}
        desabilitado={!podeNoWorkspace(s, 'integracoes.mcp') || alternar.isPending}
        aoMudar={(v) => alternar.mutate(v)}
      />
    </li>
  );
}

function Tools({ tools }: { tools: ToolMcp[] }) {
  const { t } = useTranslation('integracoes');
  // Sem useMemo: o rótulo do módulo depende do idioma e a lista agrupada não pode ficar presa no anterior.
  const grupos = (() => {
    const m = new Map<string, ToolMcp[]>();
    for (const x of tools) {
      const nome = rotuloModulo(x.modulo, x.modulo_nome);
      m.set(nome, [...(m.get(nome) ?? []), x]);
    }
    return [...m.entries()];
  })();
  return (
    <Cartao titulo={t('mcp.tools.titulo')}>
      <div className="flex flex-col gap-5">
        <p className="text-sm text-tinta-3">{t('mcp.tools.texto')}</p>
        {grupos.length === 0 ? (
          <Vazio texto={t('mcp.tools.vazio')} />
        ) : (
          grupos.map(([modulo, lista]) => (
            <section key={modulo} aria-label={modulo}>
              <div className="mb-1 flex items-center gap-2 border-b border-borda pb-2">
                <h3 className="text-sm font-semibold">{modulo}</h3>
                <span className="numero text-xs text-tinta-3">{t('mcp.tools.contagem', { ligadas: lista.filter((x) => x.ligada).length, count: lista.length })}</span>
              </div>
              <ul className="divide-y divide-borda">
                {lista.map((x) => (
                  <LinhaTool key={x.nome} tool={x} />
                ))}
              </ul>
            </section>
          ))
        )}
      </div>
    </Cartao>
  );
}

/** Lote 19/10: "Permitir conexão por OAuth" (vale para o workspace) e os clientes conectados, com Revogar. */
function LinhaConexao({ c, podeRevogar }: { c: ConexaoOAuth; podeRevogar: boolean }) {
  const { t } = useTranslation('integracoes');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const [confirmar, setConfirmar] = useState(false);
  const revogar = useMutation({
    mutationFn: () => api('POST', `/integracoes/mcp/oauth/conexoes/${c.id}/revogar`),
    onSuccess: async () => {
      setConfirmar(false);
      toast.sucesso(t('mcp.oauth.revogada'));
      await qc.invalidateQueries({ queryKey: ['integracoes', 'mcp', 'oauth'] });
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <li className="flex min-w-0 flex-col gap-2 py-3 sm:flex-row sm:items-center sm:gap-4" data-conexao-oauth>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <TextoCortado className="text-sm font-medium text-tinta-1">{nomePessoa(c.cliente_nome)}</TextoCortado>
          <span className="text-xs text-tinta-3">{nomePessoa(c.pessoa_nome)}</span>
        </div>
        <div className="mt-1 flex min-w-0 flex-wrap items-center gap-2 text-xs text-tinta-3">
          <Chips itens={escopoLegivel(tn, c.escopos)} max={6} />
        </div>
        <p className="mt-1 text-xs text-tinta-3">
          {t('mcp.oauth.ultimoUso')}: {c.ultimo_uso ? <Quando v={c.ultimo_uso} /> : t('mcp.oauth.nunca')}
        </p>
      </div>
      {podeRevogar && (
        <Botao variante="perigo" tamanho="sm" icone={<ShieldOff className="size-4" aria-hidden />} onClick={() => setConfirmar(true)}>
          {t('mcp.oauth.revogar')}
        </Botao>
      )}
      <Confirmar
        aberto={confirmar}
        aoFechar={() => setConfirmar(false)}
        perigoso
        carregando={revogar.isPending}
        aoConfirmar={() => revogar.mutate()}
        titulo={t('mcp.oauth.revogarTitulo', { cliente: c.cliente_nome })}
        texto={t('mcp.oauth.revogarTexto')}
      />
    </li>
  );
}

function ConexaoOAuthCartao({ q }: { q: ReturnType<typeof useQuery<OAuthMcp>> }) {
  const { t } = useTranslation('integracoes');
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const podeConfigurar = podeNoWorkspace(s, 'integracoes.mcp') && veTodasAsEmpresas(s);
  const alternar = useMutation({
    mutationFn: (ligado: boolean) => api('PUT', '/integracoes/mcp/oauth', { ligado }),
    onSuccess: async (_r, ligado) => {
      toast.sucesso(ligado ? t('mcp.oauth.ligado') : t('mcp.oauth.desligado'));
      await qc.invalidateQueries({ queryKey: ['integracoes', 'mcp', 'oauth'] });
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <Cartao titulo={t('mcp.oauth.titulo')}>
      <Estado consulta={q}>
        {(d) => (
          <div className="flex min-w-0 flex-col gap-4 text-sm">
            <p className="text-tinta-3">{t('mcp.oauth.texto')}</p>
            <label className="flex items-center justify-between gap-3 text-tinta-1">
              <span className="flex min-w-0 flex-col">
                <span>{t('mcp.oauth.permitir')}</span>
                <span className="text-xs text-tinta-3">{podeConfigurar || !podeNoWorkspace(s, 'integracoes.mcp') ? t('mcp.oauth.permitirAjuda') : t('mcp.oauth.soTodas')}</span>
              </span>
              <Alternador rotulo={t('mcp.oauth.permitir')} marcado={d.ligado} desabilitado={!podeConfigurar || alternar.isPending} aoMudar={(v) => alternar.mutate(v)} />
            </label>
            <section aria-label={t('mcp.oauth.conectados')} className="flex min-w-0 flex-col">
              <h3 className="border-b border-borda pb-2 text-sm font-semibold">{t('mcp.oauth.conectados')}</h3>
              {d.conexoes.length === 0 ? (
                <Vazio texto={t('mcp.oauth.vazio')} />
              ) : (
                <ul className="divide-y divide-borda">
                  {d.conexoes.map((c) => (
                    <LinhaConexao key={c.id} c={c} podeRevogar={podeNoWorkspace(s, 'integracoes.mcp')} />
                  ))}
                </ul>
              )}
            </section>
          </div>
        )}
      </Estado>
    </Cartao>
  );
}

export function Mcp() {
  const { t } = useTranslation('integracoes');
  const { t: tn } = useTranslation();
  const [aberta, setAberta] = useState<ChamadaMcp | null>(null);
  const q = useQuery({ queryKey: ['integracoes', 'mcp'], queryFn: () => get<InfoMcp>('/integracoes/mcp') });
  const oauth = useQuery({ queryKey: ['integracoes', 'mcp', 'oauth'], queryFn: () => get<OAuthMcp>('/integracoes/mcp/oauth') });
  return (
    <div className="flex flex-col gap-4">
      <Estado consulta={q}>
        {(d) => (
          <>
            <Cartao titulo={t('mcp.servidor')}>
              <div className="flex min-w-0 flex-col gap-3 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-tinta-3">{t('mcp.estadoRotulo')}</span>
                  <Selo cor="verde">{t('mcp.estado.ativo')}</Selo>
                </div>
                <p className="text-tinta-3">{t('mcp.intro')}</p>
                <LinhaCopiavel texto={d.url} />
              </div>
            </Cartao>
            <Snippets url={d.url} oauthLigado={oauth.data?.ligado ?? null} />
            <ConexaoOAuthCartao q={oauth} />
            <Tools tools={d.tools} />
          </>
        )}
      </Estado>
      <Cartao titulo={t('mcp.chamadas.titulo')} semPadding>
        <ListaPaginada<ChamadaMcp>
          chaveConsulta={['integracoes', 'chamadas-mcp']}
          caminho="/integracoes/chamadas-mcp"
          vazio={t('mcp.chamadas.vazio')}
          aoClicar={setAberta}
          colunas={[
            { chave: 'tool', rotulo: t('mcp.chamadas.tool'), render: (c) => <span className="font-mono text-xs">{c.tool}</span> },
            { chave: 'sucesso', rotulo: t('mcp.chamadas.resultado'), render: (c) => <Selo cor={c.sucesso ? 'verde' : 'perigo'}>{c.sucesso ? t('mcp.chamadas.sucesso') : t('mcp.chamadas.falha')}</Selo> },
            { chave: 'latencia', rotulo: t('mcp.chamadas.latencia'), render: (c) => <span className="numero">{`${c.latencia_ms} ms`}</span> },
            { chave: 'chave', rotulo: t('mcp.chamadas.chave'), render: (c) => <span className="text-xs">{c.chave_nome ?? c.chave_prefixo ?? '-'}</span> },
            { chave: 'quando', rotulo: t('mcp.chamadas.quando'), render: (c) => <Quando v={c.created_at} /> },
          ]}
        />
      </Cartao>
      <Modal
        aberto={!!aberta}
        aoFechar={() => setAberta(null)}
        largura="lg"
        titulo={aberta?.tool ?? ''}
        rodape={
          <Botao variante="fantasma" onClick={() => setAberta(null)}>
            {tn('comum.fechar')}
          </Botao>
        }
      >
        {aberta && (
          <div className="flex min-w-0 flex-col gap-4 text-sm">
            <div className="flex flex-wrap items-center gap-3">
              <Selo cor={aberta.sucesso ? 'verde' : 'perigo'}>{aberta.sucesso ? t('mcp.chamadas.sucesso') : t('mcp.chamadas.falha')}</Selo>
              <Quando v={aberta.created_at} />
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="kicker">{t('mcp.chamadas.argumentos')}</span>
              {aberta.ocultado === 'conteudo' ? <span className="text-tinta-3">{t('webhooks.entrega.conteudoOculto')}</span> : <Json valor={aberta.argumentos} />}
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="kicker">{t('mcp.chamadas.resultadoDetalhe')}</span>
              {aberta.ocultado === 'conteudo' ? <span className="text-tinta-3">{t('webhooks.entrega.conteudoOculto')}</span> : <Json valor={aberta.resultado} />}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
